import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useLocation, useSearchParams} from 'react-router-dom';
import {isBrowser} from 'react-device-detect';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import CameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {
    addPlaceholderQuantityGroup,
    PlaceholderConfig,
} from 'components/customer/RoomPlanner/helpers/placeholder/loadPlaceholderGLB';
import {recordPlaceholderAdd} from 'components/customer/RoomPlanner/helpers/history/placeholderHistory';
import {genericMessageHandler, Loader} from 'shared/helpers';
import {NoImageAvailable} from 'assets';
import {Image as CatalogImage} from 'shared/Image';
import {useProductContext} from 'contexts';
import {useNotificationContext} from 'contexts/NotificationContext';
import {
    PLACEHOLDER_EDIT_PARAM,
    PLACEHOLDER_FACE_PARAM,
    PLACEHOLDER_NAME_PARAM,
} from 'components/customer/RoomPlanner/components/FloatingMenu/Actions/PlaceholderEdit';
import {ItemColumn} from 'shared/components/Product/ItemColumn';
import {CategoryContainer} from 'shared/components/Product/Categories';
import {Category} from 'shared/components/Product/Category';
import {useGetPlaceholderFacesQuery} from 'components/customer/RoomPlanner/store/placeholderFaceApi';
import {
    getPlaceholderFaceImageUrl,
    PlaceholderFace,
} from 'components/customer/RoomPlanner/entity/PlaceholderFace';
import {PlaceholderQuickAdd} from 'components/customer/RoomPlanner/components/Menu/PlaceholderQuickAdd';
import {clampPlaceholderQuantity} from 'components/customer/RoomPlanner/helpers/placeholder/quantity';
import {useMouseOver} from 'shared/helpers/useMouseOver';
import {
    filterPlaceholderCatalogItems,
    getPlaceablePlaceholderItems,
    getPlaceholderCatalogSubCategory,
    getPlaceholderCategoriesWithItems,
    PlaceholderCatalogItem,
} from 'components/customer/RoomPlanner/helpers/placeholder/catalog';
import {useAppSelector} from 'store/customer';
import {selectSearchText} from 'components/customer/Product/store/productSlice';
import AppliancesIcon from 'assets/images/icons/Appliances.svg';
import CooktopIcon from 'assets/images/icons/Cooktop.svg';
import DishwasherIcon from 'assets/images/icons/Dishwasher.svg';
import FridgeIcon from 'assets/images/icons/Fridge.svg';
import MicrowaveIcon from 'assets/images/icons/Microwave.svg';
import OvenIcon from 'assets/images/icons/Oven.svg';
import RangehoodIcon from 'assets/images/icons/Rangehood.svg';
import VoidIcon from 'assets/images/icons/Void.svg';
import CooktopThumb from 'assets/images/placeholder-categories/Cooktop.png';
import DishwasherThumb from 'assets/images/placeholder-categories/Dishwasher.png';
import FridgeThumb from 'assets/images/placeholder-categories/Fridge.png';
import MicrowaveThumb from 'assets/images/placeholder-categories/Microwave.png';
import OvenThumb from 'assets/images/placeholder-categories/Oven.png';
import RangehoodThumb from 'assets/images/placeholder-categories/Rangehood.png';
import VoidThumb from 'assets/images/placeholder-categories/Void.png';
import styled from 'styled-components';

const unusedCategoryClick = () => undefined;

/**
 * Fixed thumbnails for the appliance category cards. Products keep their own
 * admin-uploaded image; the category card never borrows a product's image.
 */
const CATEGORY_THUMBS = new Map<string, string>([
    ['Oven', String(OvenThumb)],
    ['Rangehood', String(RangehoodThumb)],
    ['Dishwasher', String(DishwasherThumb)],
    ['Fridge', String(FridgeThumb)],
    ['Microwave', String(MicrowaveThumb)],
    ['Cooktop', String(CooktopThumb)],
    ['Void', String(VoidThumb)],
]);

/** Icon fallback for categories without a render (admin-added extras). */
const CATEGORY_ICONS = new Map<string, string>([
    ['Oven', String(OvenIcon)],
    ['Rangehood', String(RangehoodIcon)],
    ['Dishwasher', String(DishwasherIcon)],
    ['Fridge', String(FridgeIcon)],
    ['Microwave', String(MicrowaveIcon)],
    ['Cooktop', String(CooktopIcon)],
    ['Void', String(VoidIcon)],
]);

const toCategoryImagePath = (src: string): string =>
    src.startsWith('/') ? src.slice(1) : src;

const getCategoryThumb = (
    category: string
): {imageUrl: string; iconFit: 'contain' | 'cover'} => {
    const thumb = CATEGORY_THUMBS.get(category);

    if (thumb) {
        // Same as a cabinet category icon: the whole picture stays inside
        // the frame. Cover crops it on a wide tablet card.
        return {imageUrl: toCategoryImagePath(thumb), iconFit: 'contain'};
    }

    return {
        imageUrl: toCategoryImagePath(
            CATEGORY_ICONS.get(category) || String(AppliancesIcon)
        ),
        iconFit: 'contain',
    };
};

/* eslint-disable react/no-multi-comp -- tile renderer stays with the browse list */

type PlaceholderTileProps = {
    face: PlaceholderFace;
    config: PlaceholderConfig;
    busy: boolean;
    onAdd: (
        face: PlaceholderFace,
        config: PlaceholderConfig,
        quantity: number
    ) => Promise<void>;
    onOpenEditor: (face: PlaceholderFace) => void;
    miniBrowser: boolean;
};

const PlaceholderTile = ({
    face,
    config,
    busy,
    onAdd,
    onOpenEditor,
}: PlaceholderTileProps) => {
    const {notify} = useNotificationContext();
    const {isMouseOverComponent, handleMouseEnter, handleMouseExit} =
        useMouseOver();
    const [loading, setLoading] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [revealError, setRevealError] = useState(0);
    const messageToasted = useRef(false);
    const imageUrl = getPlaceholderFaceImageUrl(face);
    const canAdd = Boolean(CameraSetup?.scene) && !busy;
    const canCustomise = Boolean(face.is_resizable);
    const sizeLabel = [face.width, face.height, face.depth]
        .map((value) => Math.round(Number(value)))
        .filter((value) => value > 0)
        .join(' x ');

    const clearError = useCallback(() => {
        setValidationMessage('');
    }, []);

    const showErrorDetails = useCallback(() => {
        document
            .getElementById(`placeholder-item-${face.id}`)
            ?.scrollIntoView({behavior: 'smooth', block: 'start'});
        setRevealError((current) => current + 1);
    }, [face.id]);

    const handleAdd = useCallback(
        (quantity: number, width: number) => {
            if (!canAdd || loading) return Promise.resolve();
            setValidationMessage('');
            setLoading(true);
            const nextConfig =
                width > 0 ? {...config, targetWidthMm: width} : config;
            return onAdd(face, nextConfig, quantity)
                .catch((error: unknown) => {
                    const message =
                        error instanceof Error && error.message
                            ? error.message
                            : 'An unknown error occurred';
                    setValidationMessage(message);
                    return Promise.reject(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [canAdd, loading, onAdd, face, config]
    );

    useEffect(() => {
        if (validationMessage === '') {
            messageToasted.current = false;
            return;
        }
        if (messageToasted.current) return;
        messageToasted.current = true;
        genericMessageHandler(
            notify,
            {
                message: (
                    <ErrorContainer onClick={showErrorDetails}>
                        There was an error adding {face.name}, please click here
                        for more details
                    </ErrorContainer>
                ),
            },
            'danger',
            true,
            10000
        );
    }, [validationMessage, face.name, notify, showErrorDetails]);

    const handleOpenEditor = useCallback(() => {
        if (!canCustomise || busy || loading) return;
        onOpenEditor(face);
    }, [canCustomise, busy, loading, onOpenEditor, face]);

    return (
        <ItemColumn>
            <HardwareItem
                id={`placeholder-item-${face.id}`}
                $isActive={isMouseOverComponent}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseExit}>
                <Details $clickable={Boolean(isBrowser && canCustomise)}>
                    <ImageWrap
                        onClick={
                            isBrowser && canCustomise
                                ? handleOpenEditor
                                : undefined
                        }>
                        <CatalogImage
                            src={imageUrl || String(NoImageAvailable)}
                            alt={face.name}
                        />
                    </ImageWrap>
                    <Label className="item-name">
                        <div>{face.name}</div>
                        {sizeLabel ? <SizeLabel>{sizeLabel}</SizeLabel> : null}
                    </Label>
                </Details>

                <PlaceholderQuickAdd
                    faceId={face.id}
                    loading={loading}
                    disabled={!canAdd}
                    canCustomise={canCustomise}
                    errorMessage={validationMessage}
                    revealError={revealError}
                    defaultWidth={Math.round(Number(face.width)) || 0}
                    onClearError={clearError}
                    onAdd={handleAdd}
                    onClickHandler={handleOpenEditor}
                />
            </HardwareItem>
        </ItemColumn>
    );
};

type PlaceholderProductGridProps = {
    items: PlaceholderCatalogItem[];
    busy: boolean;
    miniBrowser: boolean;
    onAdd: (
        face: PlaceholderFace,
        config: PlaceholderConfig,
        quantity: number
    ) => Promise<void>;
    onOpenEditor: (face: PlaceholderFace) => void;
};

const PlaceholderProductGrid = ({
    items,
    busy,
    miniBrowser,
    onAdd,
    onOpenEditor,
}: PlaceholderProductGridProps) => (
    <CategoryContainer
        $miniBrowser={miniBrowser}
        $marginBottom={true}
        className="hardwareItems">
        {items.map(({face, config}) => (
            <PlaceholderTile
                key={face.id}
                face={face}
                config={config}
                busy={busy}
                onAdd={onAdd}
                onOpenEditor={onOpenEditor}
                miniBrowser={miniBrowser}
            />
        ))}
    </CategoryContainer>
);

const sortCatalogItems = (items: PlaceholderCatalogItem[]) =>
    [...items].sort((a, b) => a.face.name.localeCompare(b.face.name));

/**
 * Appliances browse flow (matches cabinet categories):
 * 1. Show subcategory cards that have items, with all products listed below
 * 2. Enter a subcategory to see only its items
 * @return {JSX.Element}
 */
export const PlaceholderItems: React.FC = () => {
    const {cabinetGroupListRef, placeholderListRef, setSelectedCabinetGroup} =
        useRoomPlannerContext();
    const {notify} = useNotificationContext();
    const {isMiniBrowser} = useProductContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const selectedCategory = getPlaceholderCatalogSubCategory(
        searchParams.get('subCategory')
    );
    const search = useAppSelector(selectSearchText);
    const isSearching = typeof search === 'string' && search.trim().length > 0;
    const [loadingId, setLoadingId] = useState<number | null>(null);
    const addingRef = useRef(false);
    const miniBrowser = Boolean(isMiniBrowser);

    const {
        data: placeholderFaces = [],
        isLoading,
        isError,
    } = useGetPlaceholderFacesQuery();

    const scene = CameraSetup?.scene ?? null;

    const items = useMemo(
        () => getPlaceablePlaceholderItems(placeholderFaces),
        [placeholderFaces]
    );

    const categoriesWithItems = useMemo(
        () => getPlaceholderCategoriesWithItems(placeholderFaces),
        [placeholderFaces]
    );

    const categoryItems = useMemo(
        () =>
            selectedCategory
                ? items.filter(({face}) => face.category === selectedCategory)
                : [],
        [items, selectedCategory]
    );

    const sortedItems = useMemo(() => sortCatalogItems(items), [items]);
    const sortedCategoryItems = useMemo(
        () => sortCatalogItems(categoryItems),
        [categoryItems]
    );
    const searchSource = useMemo(
        () => (selectedCategory ? categoryItems : items),
        [selectedCategory, categoryItems, items]
    );
    const searchedItems = useMemo(
        () =>
            sortCatalogItems(
                filterPlaceholderCatalogItems(searchSource, search ?? '')
            ),
        [searchSource, search]
    );

    const handleCategoryClick = useCallback(
        (category: string) => {
            const next = new URLSearchParams(location.search);
            next.set('category', 'placeholder');
            next.set('subCategory', category);
            void navigate({
                pathname: location.pathname,
                search: next.toString(),
            });
        },
        [navigate, location.search, location.pathname]
    );

    const handleCategorySelect = useCallback(
        (category: string) => () => {
            handleCategoryClick(category);
        },
        [handleCategoryClick]
    );

    const handleOpenEditor = useCallback(
        (face: PlaceholderFace) => {
            setSelectedCabinetGroup?.(null);
            const next = new URLSearchParams(location.search);
            next.set('category', 'placeholder');
            if (face.category) {
                next.set('subCategory', face.category);
            }
            next.set(PLACEHOLDER_FACE_PARAM, String(face.id));
            next.set(PLACEHOLDER_NAME_PARAM, face.name);
            next.delete(PLACEHOLDER_EDIT_PARAM);
            next.delete('product');
            next.delete('cabinetId');
            void navigate({
                pathname: location.pathname,
                search: next.toString(),
            });
        },
        [navigate, location.search, location.pathname, setSelectedCabinetGroup]
    );

    const handleAddPlaceholder = useCallback(
        async (
            face: PlaceholderFace,
            config: PlaceholderConfig,
            quantity: number
        ) => {
            if (
                !scene ||
                !placeholderListRef ||
                !cabinetGroupListRef ||
                loadingId != null ||
                addingRef.current
            ) {
                return;
            }

            const count = clampPlaceholderQuantity(quantity);
            addingRef.current = true;
            setLoadingId(face.id);
            try {
                const copies = await addPlaceholderQuantityGroup(
                    config,
                    scene,
                    placeholderListRef,
                    cabinetGroupListRef,
                    count
                );
                recordPlaceholderAdd(
                    copies,
                    placeholderListRef,
                    cabinetGroupListRef,
                    'Add'
                );
                genericMessageHandler(
                    notify,
                    {
                        message:
                            count > 1
                                ? `${count}× ${face.name} added`
                                : `${face.name} added`,
                    },
                    'success'
                );
            } catch (error) {
                const message =
                    error instanceof Error && error.message
                        ? error.message
                        : 'An unknown error occurred';
                throw new Error(message);
            } finally {
                addingRef.current = false;
                setLoadingId(null);
            }
        },
        [scene, placeholderListRef, cabinetGroupListRef, loadingId, notify]
    );

    if (isLoading) {
        return <Loader loader={true} />;
    }

    if (isError) {
        return (
            <EmptyState>
                Unable to load placeholders. Please try again.
            </EmptyState>
        );
    }

    if (items.length === 0) {
        return <EmptyState>No placeholder models configured yet.</EmptyState>;
    }

    if (isSearching) {
        if (searchedItems.length === 0) {
            return (
                <EmptyState>
                    No placeholders found <em>&ldquo;{search.trim()}&rdquo;</em>
                </EmptyState>
            );
        }

        return (
            <PlaceholderProductGrid
                items={searchedItems}
                busy={loadingId != null}
                miniBrowser={miniBrowser}
                onAdd={handleAddPlaceholder}
                onOpenEditor={handleOpenEditor}
            />
        );
    }

    // Appliances root: subcategory cards, then every product below (cabinet pattern)
    if (!selectedCategory) {
        return (
            <>
                <CategoryContainer
                    $miniBrowser={miniBrowser}
                    className="hardwareCategories">
                    {categoriesWithItems.map((category) => {
                        const productCount = items.filter(
                            ({face}) => face.category === category
                        ).length;
                        const thumb = getCategoryThumb(category);

                        return (
                            <Category
                                key={category}
                                isProduct={true}
                                isMiniBrowser={miniBrowser}
                                categoryClickHandler={unusedCategoryClick}
                                category={{
                                    id: 0,
                                    name: category,
                                    imageUrl: thumb.imageUrl,
                                    noImage: true,
                                    isSubCategory: true,
                                    productCount,
                                    action: handleCategorySelect(category),
                                    iconFit: 'contain',
                                }}
                            />
                        );
                    })}
                </CategoryContainer>
                {sortedItems.length > 0 ? (
                    <>
                        <hr />
                        <PlaceholderProductGrid
                            items={sortedItems}
                            busy={loadingId != null}
                            miniBrowser={miniBrowser}
                            onAdd={handleAddPlaceholder}
                            onOpenEditor={handleOpenEditor}
                        />
                    </>
                ) : null}
            </>
        );
    }

    if (sortedCategoryItems.length === 0) {
        return <EmptyState>No items in this category yet.</EmptyState>;
    }

    return (
        <PlaceholderProductGrid
            items={sortedCategoryItems}
            busy={loadingId != null}
            miniBrowser={miniBrowser}
            onAdd={handleAddPlaceholder}
            onOpenEditor={handleOpenEditor}
        />
    );
};

const HardwareItem = styled.div<{$isActive?: boolean}>`
    position: relative;
    border: 1px solid #e4e2db;
    border-radius: 10px;
    padding: 10px;
    background: #fdfcfa;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 10px;
    box-shadow:
        0 1px 3px rgba(0, 0, 0, 0.06),
        0 3px 10px rgba(0, 0, 0, 0.06);
    transition:
        border-color 180ms ease,
        box-shadow 180ms ease,
        transform 180ms ease;

    ${({$isActive, theme}) => {
        if ($isActive) {
            return `
                z-index: 11;
                border-color: ${theme.colors.secondary?.main};
                box-shadow:
                    0 6px 16px rgba(0, 0, 0, 0.1),
                    0 14px 32px rgba(0, 0, 0, 0.14);
                transform: translateY(-2px);
            `;
        }
    }}

    &:hover {
        z-index: 11;
        border-color: ${({theme}) => theme.colors.secondary?.main};
        box-shadow:
            0 6px 16px rgba(0, 0, 0, 0.1),
            0 14px 32px rgba(0, 0, 0, 0.14);
        transform: translateY(-2px);
    }
`;

const Details = styled.div<{$clickable?: boolean}>`
    display: flex;
    flex-direction: column;
    cursor: ${({$clickable}) => ($clickable ? 'pointer' : 'default')};
`;

/*
 * Same box on every tile. The picture keeps its own shape and only shrinks
 * when it is larger than the box, so nothing is stretched to fill it.
 */
const ImageWrap = styled.div`
    width: 100%;
    height: 120px;
    flex: 0 0 120px;
    overflow: hidden;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    border-radius: 10px;
    border: 1px solid #dfe3e8;
    box-shadow: none;

    img {
        display: block;
        flex: none;
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        box-shadow: none;
        outline: none;
    }
`;

const Label = styled.div`
    font-size: 0.9em;
    margin: 5px 0;
    font-weight: 500;
    min-height: 44px;
    color: rgb(75, 74, 74);
`;

const SizeLabel = styled.div`
    font-size: 0.9em;
    font-weight: 400;
    color: rgb(75, 74, 74);
`;

const EmptyState = styled.div`
    padding: 24px;
    text-align: center;
    color: #6b6f70;
`;

const ErrorContainer = styled.div`
    cursor: pointer;
`;

/* eslint-enable react/no-multi-comp -- tile renderer stays with the browse list */
